// Tax utility functions

/**
 * Check if taxes include any VAT-related tax
 * @param taxes Array of tax objects
 * @returns boolean
 */
export const hasVatTax = (taxes: any[]): boolean => {
  if (!taxes || taxes.length === 0) return false;
  
  return taxes.some(tax => 
    tax.name?.toLowerCase().includes('vat') || 
    tax.taxName?.toLowerCase().includes('vat') ||
    tax.type?.toLowerCase().includes('vat') ||
    tax.code?.toLowerCase() === 'vat'
  );
};

/**
 * Calculate VAT amount for a single line item
 * @param item Item with quantity and unitPrice
 * @param taxes Array of tax objects
 * @param isInclusive Whether taxes are inclusive
 * @returns Object with VAT-related calculations
 */
export const calculateItemVat = (
  item: { quantity: number; unitPrice: number },
  taxes: any[],
  isInclusive: boolean = false
) => {
  // If no taxes or item, return zeros
  if (!taxes || taxes.length === 0 || !item) {
    return {
      totalExclVat: item.quantity * item.unitPrice,
      vatAmount: 0,
      totalInclVat: item.quantity * item.unitPrice,
      vatRate: 0
    };
  }

  // Find VAT taxes
  const vatTaxes = taxes.filter(tax => 
    tax.name?.toLowerCase().includes('vat') || 
    tax.taxName?.toLowerCase().includes('vat') ||
    tax.type?.toLowerCase().includes('vat') ||
    tax.code?.toLowerCase() === 'vat'
  );

  if (vatTaxes.length === 0) {
    return {
      totalExclVat: item.quantity * item.unitPrice,
      vatAmount: 0,
      totalInclVat: item.quantity * item.unitPrice,
      vatRate: 0
    };
  }

  // Use the first VAT tax found
  const vatTax = vatTaxes[0];
  const vatRate = vatTax.rate || vatTax.taxRate || 0;
  
  // Calculate based on tax inclusion setting
  const itemTotal = item.quantity * item.unitPrice;
  
  if (isInclusive) {
    // If tax-inclusive, the displayed price already includes VAT
    // We need to calculate backwards to get the pre-VAT price
    const totalExclVat = itemTotal / (1 + vatRate / 100);
    const vatAmount = itemTotal - totalExclVat;
    
    return {
      totalExclVat,
      vatAmount,
      totalInclVat: itemTotal,
      vatRate
    };
  } else {
    // If tax-exclusive, we need to add VAT to the item price
    const totalExclVat = itemTotal;
    const vatAmount = itemTotal * (vatRate / 100);
    
    return {
      totalExclVat,
      vatAmount,
      totalInclVat: totalExclVat + vatAmount,
      vatRate
    };
  }
}; 